import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface LevelRow {
  id: number;
  level: number;
  name: string;
  min_amount: string;
  role: string;
  is_active: boolean;
}

interface LevelForm {
  level: string;
  name: string;
  min_amount: string;
  role: string;
  is_active: boolean;
}

const emptyForm: LevelForm = { level: '', name: '', min_amount: '0', role: '', is_active: true };

export function BenefitApprovalLevels() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<LevelRow[] | null>(null);
  const [editing, setEditing] = useState<'new' | LevelRow | null>(null);
  const [form, setForm] = useState<LevelForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<LevelRow[]>('/benefit-approval-levels').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: LevelRow) => {
    setForm({ level: String(item.level), name: item.name, min_amount: item.min_amount, role: item.role, is_active: item.is_active });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof LevelForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const payload = { ...form, level: Number(form.level), min_amount: Number(form.min_amount) };
    try {
      if (editing === 'new') await api.post('/benefit-approval-levels', payload);
      else if (editing) await api.put(`/benefit-approval-levels/${editing.id}`, payload);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: LevelRow) => {
    if (!(await confirmDelete(`Delete approval level "${item.name}"?`))) return;
    try {
      await api.delete(`/benefit-approval-levels/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Benefit Approval Levels</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add Level
        </button>
      </header>
      <p className="muted">
        The maker-checker chain a benefit claim passes through, in order — a level only applies once the claim's
        calculated entitlement reaches its minimum amount. Each level is decided by whoever holds its named role.
      </p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Level</th>
                <th>Name</th>
                <th>Applies From</th>
                <th>Role</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.sort((a, b) => a.level - b.level).map((item) => (
                <tr key={item.id}>
                  <td>{item.level}</td>
                  <td>{item.name}</td>
                  <td className="muted">{item.min_amount}</td>
                  <td className="muted">{item.role}</td>
                  <td className="muted">{item.is_active ? 'Yes' : 'No'}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted center">
                    No approval levels configured — no claim can currently clear approval.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add approval level' : 'Edit approval level'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Level (order) *
              <input type="number" min="1" value={form.level} onChange={set('level')} required />
              {errors.level && <small className="error">{errors.level[0]}</small>}
            </label>
            <label>
              Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label>
              Applies from amount *
              <input type="number" step="0.01" min="0" value={form.min_amount} onChange={set('min_amount')} required />
              {errors.min_amount && <small className="error">{errors.min_amount[0]}</small>}
            </label>
            <label>
              Role *
              <input value={form.role} onChange={set('role')} required placeholder="e.g. memberships_benefits_level_reviewer" />
              <small className="muted">Must be an exact role name — assign it to users under System &gt; Users.</small>
              {errors.role && <small className="error">{errors.role[0]}</small>}
            </label>
            <label className="span-2 checkbox-label">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
